import { useCallback, useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { rethrowError } from 'UIComponents/core/PromiseHandlers';
//@ts-expect-error Untyped import
import { get as getPipelines } from 'reporting-data/retrieve/inboundDb/pipelines';
import { isJourneyReport } from 'reporting-data/tsTypes/reportTypes';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
import { ObjectToDataType, getAccessibleStageObjectTypes, getStageData } from './pipelineStageOrderEditorUtils';
import StageEditorValue from './StageEditorValue';
import { getPipelineObjectOptions, updateJourneyReportWithStageSelections, STAGE_CHANGE_EVENT_NAME_V2, STAGE_CHANGE_EVENT_NAME_V3 } from './journeyStageUtils';
import { getReportEvents } from '../common/journeyHelpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const JourneyPipelineStageEditor = props => {
  const {
    onChange,
    report,
    userInfo = {
      gates: [],
      scopes: []
    }
  } = props;
  const [pipelinesByObject, setPipelinesByObject] = useState({});
  const stageObjectTypes = useMemo(() => {
    const pipelineObjects = getPipelineObjectOptions(report);
    return getAccessibleStageObjectTypes(pipelineObjects, userInfo);
  }, [report, userInfo]);
  useEffect(() => {
    const pipelinePromises = stageObjectTypes.map(object => {
      const pipelineDataType = ObjectToDataType[object];
      return getPipelines(pipelineDataType);
    });
    Promise.all(pipelinePromises).then(objectPipelines => {
      const pipelinesByObj = objectPipelines.reduce((memo, pipelines, index) => {
        memo[stageObjectTypes[index]] = pipelines;
        return memo;
      }, {});
      setPipelinesByObject(pipelinesByObj);
    }).catch(rethrowError);
  }, [stageObjectTypes]);
  const allPipelines = useMemo(() => Object.values(pipelinesByObject || {}).reduce((memo, objectPipelines /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */) => memo.concat(objectPipelines), []), [pipelinesByObject]);
  const handleChange = useCallback(newSelectedStagesWithoutLabels => {
    const labeledSelectedStages = newSelectedStagesWithoutLabels.map(selectedStage => {
      const {
        stageId
      } = selectedStage;
      const stageData = getStageData(allPipelines, stageId);
      return Object.assign({}, selectedStage, {
        label: stageData === null || stageData === void 0 ? void 0 : stageData.label
      });
    }, []);
    const updatedReport = updateJourneyReportWithStageSelections(report, labeledSelectedStages);
    onChange(updatedReport);
  }, [allPipelines, onChange, report]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormLabel, {
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "settingLabels.pipelineStageOrder.label"
      })
    }), allPipelines.length === 0 ? /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "xs"
    }) : /*#__PURE__*/_jsx(StageEditorValue, {
      allPipelines: allPipelines,
      onChange: handleChange,
      pipelinesByObject: pipelinesByObject,
      report: report
    })]
  });
};
JourneyPipelineStageEditor.isUsableForReport = report => {
  if (!isJourneyReport(report)) {
    return false;
  }
  const subAppKey = report.getIn(['displayParams', 'salesAnalytics', 'subAppKey']);
  const templateKey = report.get('templateKey');
  const isCustomized = report.get('customized');
  const isSupportedTemplate = !!subAppKey && [SUB_APPS.dealJourney, SUB_APPS.leadFunnel].includes(subAppKey) || !!templateKey && ['lead-journey-progress', 'deal-journey-progress'].includes(templateKey);
  const stepEvents = getReportEvents(report.toJS());
  const pipelineEventName = stepEvents.find(name => [STAGE_CHANGE_EVENT_NAME_V2, STAGE_CHANGE_EVENT_NAME_V3].includes(name));
  const hasBothPipelineEventsVersions = stepEvents.includes(STAGE_CHANGE_EVENT_NAME_V2) && stepEvents.includes(STAGE_CHANGE_EVENT_NAME_V3);
  if (hasBothPipelineEventsVersions) {
    console.error(`Both pipeline event names found in the report stages: ${stepEvents}`);
  }
  return isSupportedTemplate && !isCustomized && !!pipelineEventName && !hasBothPipelineEventsVersions;
};
export default JourneyPipelineStageEditor;